import {Button, Grid, Typography} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import APIServiceFormContext from '../../context/APIServiceFormContext';
import {iconColor, consistentSpacing} from './variables';

/**
 * Individual Checbox option used in CheckboxQuestion component
 *
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {any} props.selected - useState getter value
 * @param {function} props.setSelected - useState setter value
 * @param {FontAwesomeIcon?} props.startIcon - optional
 * @param {any} props.width - width prop from material UI
 * @param {any?} props.height - height prop from material UI(optional)
 *
 * @returns {React.Component}
 */
const CheckboxSelectButton = ({title, subtitle, selected, setSelected, startIcon, width, height}) => (
  <Button
    color={selected ? 'graydetailsselected' : 'graydetailsnotselected'}
    sx={{
      color: 'black',
      boxSizing: 'border-box',
      boxShadow: 'none',
      borderRadius: 3,
      fontSize: 14,
      border: selected ? `6px solid ${iconColor}` : '6px solid transparent',
      borderWidth: 3,
      minHeight: height === undefined ? '100px' : height,
      width: width,
      py: 1,
    }}
    size="medium"
    variant="contained"
    onClick={setSelected}
  >
    <Grid container alignItems="center" gap={2}>
      <Grid
        item
        xs={2}
        sx={{
          display: startIcon === undefined ? 'none' : 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {startIcon}
      </Grid>
      <Grid item xs={startIcon === undefined ? 12 : 7} textAlign={startIcon === undefined ? 'center' : 'left'}>
        <Typography sx={{fontWeight: 450}}>{title}</Typography>
        <Typography variant="body2" component="div" sx={{display: subtitle === undefined ? 'none' : 'normal'}}>
          {subtitle}
        </Typography>
      </Grid>
    </Grid>
  </Button>
);

/**
 * CheckboxQuestion
 * Component that renders a question title as well as some multiple choice options that can include icons main text and additional text. In order to show multiple options we use the Grid layout from material UI, that's why of the childXS,SM,MD props. State is not being managed directly in this component, it should be done from the parent component.See this directory's README.md for more details.
 *
 * @param {Object} props - Component props
 * @param {title} props.title - Question title
 * @param {Object?} props.titleSX - sx prop from material UI that will be passed to the title component(optional)
 * @param {Array.<{
 *   title: string
 *   subtitle: string // optional
 *   value: any
 *   icon: FontAwesomeIcon // optional
 * }>} props.choices - Array representing what data is needed from the choices that this question will have
 * @param {number} props.childXS - column width of each option in the xs breakpoint of material UI
 * @param {number} props.childSM - column width of each option in the sm breakpoint of material UI
 * @param {number} props.childMD - column width of each option in the md breakpoint of material UI
 * @param {number} props.childLG - column width of each option in the lg breakpoint of material UI
 * @param {function} props.additionaButOptionaValidation - callback that has the array of selected values as an input and allows the user to add additional error messages based on whatever you want to process there, you just return a string with the error message and if the error happens it wil be shown after clicking next
 * @param {any} props.itemHeight - prop passed out to the height prop of the material UI component of each option as the minHeight
 * @param {Context} props.context - context where the form's "logic" will be stored
 *
 * @returns {React.Component}
 */
export const CheckboxQuestion = ({
  title,
  titleSX,
  subtitle,
  choices,
  childXS,
  childSM,
  childMD,
  childLG,
  additionaButOptionaValidation,
  itemHeight,
  apiName,
}) => {
  const {setValidationFunc, setSendDataArgs, savedValue} = useContext(APIServiceFormContext);
  const [selected, setSelected] = useState(savedValue === undefined || !Array.isArray(savedValue) ? [] : savedValue);
  const xs = childXS === undefined ? 12 : childXS;
  const sm = childSM === undefined ? 6 : childSM;
  const md = childMD === undefined ? sm : childMD;
  const lg = childLG === undefined ? md : childLG;

  const componentValidationWrapper = () => {
    function componentValidation() {
      if (selected.length === 0) return 'Please select an option.';

      return additionaButOptionaValidation?.(selected) || '';
    }

    return componentValidation;
  };

  useEffect(() => {
    setValidationFunc(componentValidationWrapper);
    setSendDataArgs({data: selected});
  }, [selected]);
  useEffect(() => {
    setSelected(savedValue === undefined || !Array.isArray(savedValue) ? [] : savedValue);
  }, [savedValue]);
  return (
    <Grid container rowGap={consistentSpacing} sx={{width: '100%', maxWidth: '750px'}}>
      <Grid item xs={12}>
        <Typography component={'p'} variant="subtitle0" mt={{xs: 0, sm: 3}} sx={titleSX}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography component={'p'} variant="subtitle0" my={{xs: 0, sm: 2}}>
          {subtitle}
        </Typography>
      </Grid>
      <Grid
        item
        container
        gap={consistentSpacing}
        sx={{
          width: '100%',
          maxWidth: '750px',
          boxSizing: 'border-box',
          justifyContent: 'space-between',
        }}
      >
        {choices.map((choice, index) => (
          <Grid
            item
            //default grid row/column values
            xs={xs}
            sm={sm}
            md={md}
            lg={lg}
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CheckboxSelectButton
              title={choice.title}
              subtitle={apiName === 'hehealth' ? choice.value : choice.subtitle}
              selected={apiName === 'hehealth' ? selected.includes(choice) : selected.includes(choice.value)}
              height={itemHeight}
              width={'100%'}
              setSelected={() => {
                const alreadyIncluded =
                  apiName === 'hehealth' ? selected.includes(choice) : selected.includes(choice.value);

                if (apiName === 'hehealth') {
                  const newSelected = !alreadyIncluded ? [...selected, choice] : selected.filter(s => s !== choice);
                  setSelected(newSelected);
                } else {
                  const newSelected = !alreadyIncluded
                    ? [...selected, choice.value]
                    : selected.filter(s => s !== choice.value);
                  setSelected(newSelected);
                }
              }}
              startIcon={choice.icon}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
